<template>
  <SearchAndFilterWrapper
    :search-label="'Buchung suchen...'"
    :dialog-title="'Filter Buchungen'"
    :applied-filters="appliedFilters"
    :apply-filters-fn="applyFilters"
    :reset-pending-fn="resetPending"
    :remove-applied-filter-fn="removeAppliedFilter"
    :clear-all-fn="clearAll"
    :handle-export-fn="handleExport"
    :show-booking-export="true"
    @update:search="handleSearch"
  >
    <template v-slot:content>
      <div class="mb-2">
        <v-row>
          <v-col cols="12" sm="6">
            <div class="text-subtitle-1 mb-2">Buchungsstatus</div>
            <v-select
              v-model="pending.status.bookingStatus"
              :items="bookingStatusOptions"
              placeholder="Status"
              variant="solo"
              density="comfortable"
              clearable
              hide-details
            ></v-select>
          </v-col>
          <v-col cols="12" sm="6">
            <div class="text-subtitle-1 mb-2">Zahlungsstatus</div>
            <v-select
              v-model="pending.status.isPayed"
              :items="paymentStatusOptions"
              placeholder="Bezahlt"
              variant="solo"
              density="comfortable"
              clearable
              hide-details
            ></v-select>
          </v-col>
        </v-row>
      </div>
      <v-row>
        <v-col cols="12" sm="8" style="min-width: 0">
          <div class="text-subtitle-1 mb-2">Buchbares Element</div>
          <v-select
            v-model="pending.selectedBookable"
            :items="api.bookables"
            item-title="title"
            item-value="id"
            placeholder="Buchbares Element auswählen"
            variant="solo"
            density="comfortable"
            clearable
            hide-details
            :menu-props="{
              maxWidth: '360px',
            }"
          ></v-select>
        </v-col>
        <v-col cols="12" sm="4">
          <div class="text-subtitle-1 mb-2">Buchbarer Typ</div>
          <v-select
            v-model="pending.selectedBookableType"
            :items="bookableTypeOptions"
            item-title="name"
            item-value="value"
            placeholder="Typ auswählen"
            variant="solo"
            density="comfortable"
            clearable
            hide-details
          ></v-select>
        </v-col>
      </v-row>
      <v-row class="mb-2">
        <v-col cols="12" sm="6">
          <div class="text-subtitle-1 mb-2">Buchbare Nutzer</div>
          <v-combobox
            v-model="pending.selectedUsers"
            :items="api.users"
            placeholder="Nutzer auswählen oder eingeben"
            clearable
            variant="solo"
            density="comfortable"
            hide-details
            :return-object="false"
          >
          </v-combobox>
        </v-col>
        <v-col cols="12" sm="6">
          <div class="text-subtitle-1 mb-2">Benutzergruppen</div>
          <v-select
            v-model="pending.selectedUserGroup"
            :items="api.userGroups"
            placeholder="Verfügbar für Benutzergruppen"
            item-title="name"
            item-value="id"
            clearable
            variant="solo"
            density="comfortable"
            hide-details
            :return-object="false"
          >
            <template v-slot:item="{ item, props }">
              <v-list-item v-bind="props">
                <template v-slot:title>
                  <div class="d-flex align-center" style="gap: 0.5rem">
                    <span>{{ item.raw ? item.raw.name : item.name }}</span>
                    <span
                      class="text-caption"
                      style="color: rgba(0, 0, 0, 0.6)"
                    >
                      ({{
                        (item.raw ? item.raw.tenantId : item.tenantId) === null
                          ? "Instanz-Ebene"
                          : "Mandanten-Ebene"
                      }})
                    </span>
                  </div>
                </template>
              </v-list-item>
            </template>
          </v-select>
        </v-col>
      </v-row>
      <div>
        <div class="text-subtitle-1 mb-2">Zeitraum</div>
        <v-row>
          <v-col cols="12" sm="6">
            <v-menu
              v-model="menuBegin"
              :close-on-content-click="false"
              transition="scale-transition"
              min-width="auto"
            >
              <template v-slot:activator="{ props }">
                <v-text-field
                  v-model="pending.timeBegin"
                  placeholder="Von"
                  prepend-icon="mdi-calendar"
                  readonly
                  v-bind="props"
                  variant="solo"
                  density="comfortable"
                ></v-text-field>
              </template>
              <v-date-picker
                v-model="pending.timeBegin"
                hide-title
                @update:modelValue="menuBegin = false"
              ></v-date-picker>
            </v-menu>
          </v-col>
          <v-col cols="12" sm="6">
            <v-menu
              v-model="menuEnd"
              :close-on-content-click="false"
              transition="scale-transition"
              min-width="auto"
            >
              <template v-slot:activator="{ props }">
                <v-text-field
                  v-model="pending.timeEnd"
                  placeholder="Bis"
                  prepend-icon="mdi-calendar"
                  readonly
                  v-bind="props"
                  variant="solo"
                  density="comfortable"
                ></v-text-field>
              </template>
              <v-date-picker
                v-model="pending.timeEnd"
                hide-title
                @update:modelValue="menuEnd = false"
              ></v-date-picker>
            </v-menu>
          </v-col>
        </v-row>
      </div>
    </template>
  </SearchAndFilterWrapper>
</template>

<script>
import ApiBookablesService from "../../services/api/ApiBookablesService";
import { mapGetters } from "vuex";
import ApiTenantService from "../../services/api/ApiTenantService";
import ApiUserGroupService from "../../services/api/ApiUserGroupService";
import SearchAndFilterWrapper from "@/components/Filters/SearchAndFilterWrapper.vue";

export default {
  name: "BookingSearchAndFilter",
  components: { SearchAndFilterWrapper },
  props: { type: String },
  data() {
    return {
      localSearch: "",
      menuBegin: false,
      menuEnd: false,
      api: { users: [], userGroups: [] },
      pending: {
        status: { bookingStatus: null, isPayed: null },
        selectedBookable: null,
        selectedBookableType: null,
        selectedUsers: null,
        selectedUserGroup: null,
        timeBegin: null,
        timeEnd: null,
      },
      applied: {},
      bookingStatusOptions: [
        "ausstehend",
        "abgelehnt",
        "storniert",
        "freigegeben",
      ],
      paymentStatusOptions: ["bezahlt", "ausstehend"],
      bookableTypeOptions: [
        // { name: "Veranstaltungsorte", value: "event-location" },
        { name: "Räume", value: "room" },
        { name: "Ressourcen", value: "resource" },
        { name: "Ticket", value: "ticket" },
      ],
    };
  },
  watch: {
    async tenantId() {
      this.fetchTenantUsers();
      this.fetchBookables();
      this.fetchUserGroups();
    },
    "$route.query": {
      deep: true,
      handler(newQuery) {
        const filters = this.queryToFilters(newQuery);
        this.applied = JSON.parse(JSON.stringify(filters));
        this.pending = JSON.parse(JSON.stringify(filters));
        this.$emit("update:filters", this.applied);
      },
    },
  },
  computed: {
    ...mapGetters({
      tenantId: "tenants/currentTenantId",
    }),
    appliedFilters() {
      const chips = [];
      const a = this.applied;

      if (a.status?.bookingStatus)
        chips.push({
          type: "status",
          key: "bookingStatus",
          label: `Status: ${a.status.bookingStatus}`,
        });
      if (a.status?.isPayed)
        chips.push({
          type: "status",
          key: "isPayed",
          label: `Bezahlt: ${a.status.isPayed}`,
        });
      if (a.selectedBookable)
        chips.push({
          type: "bookable",
          label: `Buchbares: ${this.getBookableTitle(a.selectedBookable)}`,
        });
      if (a.selectedBookableType)
        chips.push({
          type: "bookableType",
          label: `Typ: ${
            this.bookableTypeOptions?.filter(
              (typ) => typ.value === a.selectedBookableType
            )[0]?.name
          }`,
        });
      if (a.selectedUsers)
        chips.push({
          type: "user",
          value: a.selectedUsers,
          label: `Nutzer: ${a.selectedUsers}`,
        });
      if (a.selectedUserGroup)
        chips.push({
          type: "userGroup",
          value: a.selectedUserGroup,
          label: `Benutzergruppe: ${
            this.api.userGroups?.filter(
              (group) => group.id === a.selectedUserGroup
            )[0]?.name ?? a.selectedUserGroup
          }`,
        });
      if (a.timeBegin || a.timeEnd)
        chips.push({
          type: "date",
          label: `Zeitraum: ${
            a.timeBegin
              ? Intl.DateTimeFormat("de-DE", {
                  dateStyle: "short",
                }).format(new Date(a.timeBegin))
              : "—"
          } bis ${
            a.timeEnd
              ? Intl.DateTimeFormat("de-DE", {
                  dateStyle: "short",
                }).format(new Date(a.timeEnd))
              : "—"
          }`,
        });

      return chips;
    },
  },
  methods: {
    async fetchTenantUsers() {
      try {
        const response = await ApiTenantService.getTenantUsers(this.tenantId);
        const users = [
          ...response.users.map((u) => u.userId),
          ...response.owner,
        ];
        this.api.users = [...new Set(users)];
      } catch (error) {
        console.error("Error fetching users:", error);
      }
    },
    async fetchBookables() {
      try {
        const response = await ApiBookablesService.getBookables();
        this.api.bookables = response.data;
      } catch (error) {
        console.error("Error fetching bookables:", error);
      }
    },
    async fetchUserGroups() {
      await ApiUserGroupService.getUserGroups().then((result) => {
        this.api.userGroups = result;
      });
    },
    handleExport(exportFormat) {
      const filters = { ...this.applied };
      const query = this.filtersToQuery(filters);
      if (this.localSearch?.trim()) {
        query.search = this.localSearch.trim();
      }
      const params = new URLSearchParams(query).toString();
      let url = "";
      if (exportFormat === "csv") {
        url = `${process.env.VUE_APP_SERVER_BASE_URL}/csv/${this.tenantId}/bookings?${params}`;
      } else {
        url = `${process.env.VUE_APP_SERVER_BASE_URL}/excel/${this.tenantId}/bookings?${params}`;
      }

      window.open(url, "_blank");
    },
    removePendingUser() {
      this.pending.selectedUsers = null;
    },
    removeAppliedFilter(chip) {
      if (chip.type === "status") {
        this.applied.status[chip.key] = null;
      } else if (chip.type === "user") {
        this.applied.selectedUsers = null;
      } else if (chip.type === "userGroup") {
        this.applied.selectedUserGroup = null;
      } else if (chip.type === "bookable") {
        this.applied.selectedBookable = null;
      } else if (chip.type === "bookableType") {
        this.applied.selectedBookableType = null;
      } else if (chip.type === "date") {
        this.applied.timeBegin = null;
        this.applied.timeEnd = null;
      }

      this.$emit("update:filters", this.applied);
      this.updateUrlFromFilters(this.applied);
    },
    applyFilters() {
      this.applied = JSON.parse(JSON.stringify(this.pending));
      this.$emit("update:filters", this.applied);
      this.updateUrlFromFilters(this.applied);
    },
    clearAll() {
      this.pending = {
        status: { bookingStatus: null, isPayed: null },
        selectedBookable: null,
        selectedBookableType: null,
        selectedUsers: null,
        selectedUserGroup: null,
        timeBegin: null,
        timeEnd: null,
      };
      this.applied = JSON.parse(JSON.stringify(this.pending));
      this.$emit("update:filters", this.applied);
      this.updateUrlFromFilters(this.applied);
    },
    resetPending() {
      this.pending = JSON.parse(JSON.stringify(this.applied));
    },
    getBookableTitle(id) {
      const b = this.api?.bookables?.find((b) => b.id === id);
      return b ? b.title : id;
    },
    filtersToQuery(filters) {
      const query = {};

      if (filters.status?.bookingStatus)
        query.status = filters.status.bookingStatus;
      if (filters.status?.isPayed) query.isPayed = filters.status.isPayed;
      if (filters.selectedBookable) query.bookable = filters.selectedBookable;
      if (filters.selectedBookableType)
        query.bookableType = filters.selectedBookableType;
      if (filters.selectedUsers) query.users = filters.selectedUsers;
      if (filters.selectedUserGroup)
        query.userGroup = filters.selectedUserGroup;
      if (filters.timeBegin) query.timeBegin = filters.timeBegin;
      if (filters.timeEnd) query.timeEnd = filters.timeEnd;

      return query;
    },
    queryToFilters(query) {
      return {
        status: {
          bookingStatus: query.status || null,
          isPayed: query.isPayed || null,
        },
        selectedBookable: query.bookable || null,
        selectedBookableType: query.bookableType || null,
        selectedUsers: query.users,
        selectedUserGroup: query.userGroup,
        timeBegin: query.timeBegin || null,
        timeEnd: query.timeEnd || null,
      };
    },
    updateUrlFromFilters(filters) {
      const query = this.filtersToQuery(filters);
      this.$router.replace({ query });
    },
    handleSearch(value) {
      this.localSearch = value;
      this.$emit("update:search", value);
    },
  },
  mounted() {
    this.fetchTenantUsers();
    this.fetchBookables();
    this.fetchUserGroups();

    const initialFilters = this.queryToFilters(this.$route.query);
    this.applied = JSON.parse(JSON.stringify(initialFilters));
    this.pending = JSON.parse(JSON.stringify(initialFilters));
    this.$emit("update:filters", this.applied);
  },
};
</script>

<style scoped lang="scss">
::v-deep .active-button {
  color: white !important;
  background-color: #0099db !important;
}
.truncate-chip {
  max-width: 160px;
  display: flex;
  align-items: center;
}

.chip-text {
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  flex: 1;
  min-width: 0;
}
</style>
